import { RhFileUploader, RhIcon } from "@rhythm-ui/react";
import React, { useState } from "react";

import api from "@/services/api";

import showToastMessages from "@/helpers/showToastMessages";

import { Button } from "@/components/ui/Button";

interface FileType {
  id: number;
  name: string;
  type: string;
  meta: { id: number };
}

const JDFileUploader = ({
  pdfId,
  createdJobId,
  alreadyUploadedResume,
}: {
  pdfId: React.MutableRefObject<number | null>;
  createdJobId: number | null;
  alreadyUploadedResume: string | undefined;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(!!createdJobId);
  const [resumeUrl, setResumeUrl] = useState<null | string>(null);

  const resumePreUpload = async (files: FileType[]) => {
    try {
      setIsUploading(true);
      const response = await api.company.uploadJobPdf({
        file_name: files[0].name,
      });
      const file = response.data[0];
      pdfId.current = file.id;

      return response.data.map((file: any, i: number) => {
        const data = {
          id: file.id,
          fileId: files[i].id,
          endpoint: file.bucket_url,
          method: "POST",
          fileIdentity: files[i].id,
          metaFields: {
            "X-Amz-Signature": file.aws_policy_signature,
            Key: `${file.upload_path}${file.slug}`,
            "X-Amz-Credential": file.x_amz_credential,
            "X-Amz-Date": file.x_amz_date,
            "X-amz-server-side-encryption": "AES256",
            "X-Amz-Algorithm": "AWS4-HMAC-SHA256",
            "Content-Type": files[i].type,
            Policy: file.aws_policy,
          },
        };
        return data;
      });
    } catch (error) {
      showToastMessages(error);
    } finally {
      setIsUploading(false);
    }
  };

  const resumePostUpload = async () => {
    if (typeof pdfId.current !== "number")
      return Promise.reject(new Error("Invalid fileId"));

    try {
      const res = await api.company.updateJobPdfStatus(pdfId.current, {
        upload_status: "Completed",
      });

      setResumeUrl(res.data.url);
      setIsUploadSuccessful(true);
    } catch (error) {
      showToastMessages(error);
    }
  };

  if (isUploadSuccessful) {
    return (
      <>
        <div className="flex w-full items-center justify-between rounded-sm border border-dashed border-gray-200 px-tm2 py-tnano">
          <p>PDF Uploaded</p>

          <div className="flex items-center gap-tnano">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() =>
                (resumeUrl || alreadyUploadedResume) &&
                window.open(resumeUrl ?? alreadyUploadedResume, "_blank")
              }
            >
              <RhIcon icon="fluent:open-16-filled" />
              View
            </Button>

            <Button
              type="button"
              variant={"tertiaryBlue"}
              onClick={() => setIsUploadSuccessful(false)}
            >
              <RhIcon icon="tabler:upload" />
              Upload New
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={`mt-tm3 flex w-full bg-[#f4f4f4] [&>div]:w-full`}>
      {!isUploading ? (
        <RhFileUploader
          height="168px"
          preUpload={resumePreUpload}
          postUpload={resumePostUpload}
          restrictions={{
            allowedFileTypes: [".pdf"],
            maxNumberOfFiles: 1,
          }}
          width="100%"
          autoProceed
          isUpload
        />
      ) : (
        <div className="flex h-[168px] w-full items-center justify-center rounded border border-dashed">
          <h5>Uploading...</h5>
        </div>
      )}
    </div>
  );
};

export default JDFileUploader;
